// TODO: If you make edits to this file you must deal with these ESLint rules
/*eslint-disable @typescript-eslint/no-use-before-define */

import React from 'react';
import {
  RelayPaginationProp,
  createPaginationContainer,
  graphql,
} from 'react-relay';
import { FragmentRefs } from 'relay-runtime';

import styled from '@emotion/styled';

import { News_viewer } from '@zego-types/News_viewer.graphql';
import { BREAKPOINTS, disabledColor, getColorTint, unit } from '@zego/theme';

import H2 from '@zego/components/atoms/H2';
import Spacer from '@zego/components/atoms/Spacer';
import View from '@zego/components/atoms/View';
import BlogPostCard from '@zego/components/organisms/BlogPostCard';
import ContentContainer from '@zego/components/SCContentContainer';

import Arrow from '@zego/static/svgs/next-arrow.svg';

const NEWS_COPY = {
  title: 'Latest News',
  moreLabel: 'See more posts',
  noPostsMessage: "We haven't published anything yet.",
};

interface PostEdge {
  readonly cursor: string;
  readonly node: {
    readonly ' $fragmentRefs': FragmentRefs<'BlogPostCard_post'>;
  };
}

interface NewsProps {
  viewer: News_viewer;
  relay: RelayPaginationProp;
  tags?: string[];
  title?: string;
}

function News({ viewer, relay, title }: NewsProps): JSX.Element {
  const hasPosts = viewer.posts ? viewer.posts.edges.length > 0 : false;
  const getMorePosts = (): void => {
    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }
    relay.loadMore(6);
  };

  return (
    <View
      css={{
        display: 'flex',
        backgroundColor: disabledColor(3),
        alignItems: 'center',
        flexDirection: 'column',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        padding: unit(2),
        [BREAKPOINTS.medium]: {
          padding: unit(2),
        },
        [BREAKPOINTS.large]: {
          padding: unit(4),
        },
      }}
    >
      <Spacer height={unit(6)} />
      <ContentContainer
        css={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <H2
          css={{
            textAlign: 'center',
            fontSize: 30,
            [BREAKPOINTS.medium]: { fontSize: 40 },
          }}
        >
          {title || NEWS_COPY.title}
        </H2>
        <Spacer height={unit(3)} />
        {hasPosts ? (
          <NewsLayout>
            {viewer.posts?.edges
              .filter(edge => !!edge?.node)
              .map(({ cursor, node }: PostEdge) => (
                <BlogPostCard key={cursor} post={node} />
              ))}
          </NewsLayout>
        ) : (
          <p>{NEWS_COPY.noPostsMessage}</p>
        )}
        <Spacer height={unit(3)} />
        {relay?.hasMore() && (
          <>
            <GetMoreButton onClick={getMorePosts}>
              <span>{NEWS_COPY.moreLabel}</span>{' '}
              <DownArrow src={Arrow} alt="Down arrow" />
            </GetMoreButton>
            <Spacer height={unit(6)} />
          </>
        )}
      </ContentContainer>
    </View>
  );
}

export default createPaginationContainer(
  News,
  {
    viewer: graphql`
      fragment News_viewer on Viewer
        @argumentDefinitions(
          count: { type: "Int", defaultValue: 6 }
          cursor: { type: "String" }
          tags: { type: "[String]", defaultValue: null }
        ) {
        posts: blogPosts(first: $count, after: $cursor, tags: $tags)
          @connection(key: "News_posts") {
          edges {
            cursor
            node {
              ...BlogPostCard_post
            }
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.viewer && props.viewer.posts;
    },
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }, fragmentVariables) {
      return {
        count,
        cursor,
        tags: fragmentVariables.tags,
      };
    },
    query: graphql`
      query NewsQuery($count: Int!, $cursor: String, $tags: [String]) {
        viewer {
          ...News_viewer @arguments(count: $count, cursor: $cursor, tags: $tags)
        }
      }
    `,
  },
);

/**
 * Styled components
 */

const GetMoreButton = styled('button')({
  border: 0,
  background: 'transparent',
  color: getColorTint('grey', 11),
  fontSize: 16,
  cursor: 'pointer',
  transition: 'transform 0.1s ease-in-out',
  '&:focus,&:hover': {
    transform: 'scale(1.1)',
  },
});

const DownArrow = styled('img')({
  marginLeft: unit(1),
  transform: 'rotate(90deg)',
});

const NewsLayout = styled(View)({
  display: 'grid',
  gridTemplateColumns: '1fr',
  rowGap: unit(3),
  padding: unit(1),
  [BREAKPOINTS.medium]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: unit(4),
    padding: unit(2),
  },
  [BREAKPOINTS.large]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: unit(8),
    padding: unit(4),
  },
});
